/**
 * Morning Snapshot metrics — derived client-side from the existing
 * `CaseSummary[]` payload. When the backend ships
 * GET /api/v1/verification-dashboard-summary (see
 * docs/backend-brief-verification-dashboard-summary.md), this helper will
 * become a fallback used only when the endpoint 404s.
 *
 * Window: avg-time-to-complete uses a rolling 30-day window keyed on the
 * proxy submission timestamp (`updated_at` for terminal states, since the
 * current `CaseSummary` does not expose a true `submitted_at`).
 */

import type { CaseSummary } from "@/lib/api";
import { getCaseStatus, isClosedCase } from "@/lib/caseStatus";

export interface SnapshotMetrics {
  needs_review_count: number;
  needs_attention_count: number;
  waiting_on_applicant_count: number;
  /**
   * Sub-count of `waiting_on_applicant_count` — applicant has not yet
   * submitted (i.e. still owes OAuth connections or vouch invites).
   *
   * Proxy: `kind === "action_required"` AND `current_step < 5`. Replace
   * with a server-side rollup once backend §10.4
   * (`needs_to_submit_count`) ships.
   */
  needs_to_submit_count: number;
  /**
   * Sub-count of `waiting_on_applicant_count` — applicant has submitted
   * and is waiting on vouches to complete.
   *
   * Proxy: `kind === "action_required"` AND `current_step >= 5`. Replace
   * with a server-side rollup once backend §10.4
   * (`awaiting_vouch_count`) ships. Invariant:
   * `needs_to_submit_count + awaiting_vouch_count === waiting_on_applicant_count`.
   */
  awaiting_vouch_count: number;
  /**
   * Count of cases where one or more requested vouches are still pending.
   * BACKEND GAP: `CaseSummary` does not yet expose `vouch_summary.status`
   * (suggested values: not_requested | requested | pending | completed |
   * declined | unable_to_confirm | expired). Returns 0 today; wire up once
   * the backend ships the field.
   */
  waiting_on_vouch_count: number;
  /** Alias of needs_attention_count — cases idle 2+ days. */
  idle_count: number;
  /** Average days from invite → submission. `null` when no qualifying cases. */
  avg_time_to_complete_days: number | null;
  /** @deprecated Use `review_complete_rate_percent`. */
  completion_rate_percent: number | null;
  /** (needsReview + verified + notVerified) / invited × 100. `null` when invited === 0. */
  submitted_rate_percent: number | null;
  /** @deprecated Use `review_complete_rate_percent`. */
  outcome_rate_percent: number | null;
  /** (verified + notVerified) / invited × 100. `null` when invited === 0. */
  review_complete_rate_percent: number | null;
  dropped_off_count: number;
  verified_count: number;
  not_verified_count: number;
  /** verified / (verified + not_verified) × 100. `null` when no completed cases. */
  verified_rate_percent: number | null;
}


const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

function tsMs(iso?: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

function lastActivityMs(c: CaseSummary): number | null {
  return tsMs(c.declined_at) ?? tsMs(c.updated_at) ?? tsMs(c.created_at);
}

/**
 * Shared predicate used by both `computeSnapshot` and the Dashboard's
 * synthetic "Needs Attention" filter. Returns true when the case is
 * actionable (`pending` / `in_progress` / `action_required`) AND has not
 * had any recorded activity in the last 2 days.
 *
 * Backend caveat: there is no true `last_activity_at` on `CaseSummary`
 * today — we approximate as `declined_at ?? updated_at ?? created_at`.
 * Documented in docs/CLAUDE-verification-center-layout-2026-05-29.md.
 */
export function isNeedsAttention(c: CaseSummary, now: number = Date.now()): boolean {
  if (isClosedCase(c)) return false;
  const kind = getCaseStatus(c).kind;
  if (kind !== "pending" && kind !== "in_progress" && kind !== "action_required") {
    return false;
  }
  const la = lastActivityMs(c);
  return la !== null && now - la >= TWO_DAYS_MS;
}

export function computeSnapshot(cases: CaseSummary[]): SnapshotMetrics {
  const now = Date.now();

  let needsReview = 0;
  let needsAttention = 0;
  let waitingOnApplicant = 0;
  let needsToSubmit = 0;
  let awaitingVouch = 0;
  let droppedOff = 0;
  let verified = 0;
  let notVerified = 0;
  let totalCompletionDays = 0;
  let completionDaysCount = 0;

  for (const c of cases) {
    const kind = getCaseStatus(c).kind;
    const closed = isClosedCase(c);

    if (closed) {
      droppedOff += 1;
      continue;
    }

    if (kind === "submitted") needsReview += 1;
    if (kind === "action_required") {
      waitingOnApplicant += 1;
      // Proxy split — see SnapshotMetrics docstring. Step 5 is the
      // submit/review step in the applicant flow; cases at >= 5 have
      // submitted and are waiting on vouches to complete.
      if ((c.current_step ?? 0) >= 5) {
        awaitingVouch += 1;
      } else {
        needsToSubmit += 1;
      }
    }
    if (kind === "verified") verified += 1;
    if (kind === "not_verified") notVerified += 1;

    if (isNeedsAttention(c, now)) needsAttention += 1;

    if (kind === "verified" || kind === "not_verified") {
      const created = tsMs(c.created_at);
      // No `submitted_at` on CaseSummary today — use `updated_at` as a
      // proxy. Backend brief asks for a true `submitted_at` field.
      const submitted = tsMs(c.updated_at);
      if (
        created !== null &&
        submitted !== null &&
        submitted >= created &&
        now - submitted <= THIRTY_DAYS_MS
      ) {
        totalCompletionDays += (submitted - created) / DAY_MS;
        completionDaysCount += 1;
      }
    }
  }

  const completed = verified + notVerified;
  const submitted = needsReview + verified + notVerified;
  const invited = cases.length;
  const outcomeRate =
    invited > 0 ? Math.round((completed / invited) * 100) : null;
  const submittedRate =
    invited > 0 ? Math.round((submitted / invited) * 100) : null;

  const verifiedRate =
    completed > 0 ? Math.round((verified / completed) * 100) : null;

  const avgDays =
    completionDaysCount > 0
      ? Math.round((totalCompletionDays / completionDaysCount) * 10) / 10
      : null;

  return {
    needs_review_count: needsReview,
    needs_attention_count: needsAttention,
    waiting_on_applicant_count: waitingOnApplicant,
    needs_to_submit_count: needsToSubmit,
    awaiting_vouch_count: awaitingVouch,
    // Proxy until backend ships `vouch_summary.status`: applicants who
    // have submitted (action_required at step >= 5) are effectively
    // "waiting on vouch".
    waiting_on_vouch_count: awaitingVouch,
    idle_count: needsAttention,
    avg_time_to_complete_days: avgDays,
    completion_rate_percent: outcomeRate,
    submitted_rate_percent: submittedRate,
    outcome_rate_percent: outcomeRate,
    review_complete_rate_percent: outcomeRate,
    dropped_off_count: droppedOff,
    verified_count: verified,
    not_verified_count: notVerified,
    verified_rate_percent: verifiedRate,
  };
}

